import React from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { formatMoney, formatAddress } from "@/lib/format";

/* Fix T (2026-10-05): vista de la factura para el CLIENTE, sin login. Es otra cosa que el
   link del subcontratista (/ot/…): muestra solo la factura (marca, número, fechas, líneas
   con precio al cliente y total), nunca costos internos ni datos del subcontratista.
   En inglés porque la leen los administradores de los edificios. */
const client = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public/qbo` });

const fecha = (iso) => {
  if (!iso) return "—";
  const solo = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = solo ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function PublicInvoicePage() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-invoice", token],
    queryFn: async () => (await client.get(`/factura/${token}`)).data,
    retry: false,
  });

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-surface-sunken text-[14px] text-ink-tertiary">Loading invoice…</div>;
  }
  if (isError || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-6" data-testid="public-invoice-error">
        <p className="max-w-sm text-center text-[15px] text-ink-secondary">
          This invoice link is not available. Please contact us and we will send you a new one.
        </p>
      </div>
    );
  }

  const { marca, cliente, orden } = data;
  const saldo = data.pagada ? 0 : data.total_cents;

  return (
    <div className="min-h-screen bg-surface-sunken px-4 py-8 print:bg-white print:p-0" data-testid="public-invoice">
      <div className="mx-auto max-w-[760px] border border-line bg-white p-8 print:border-0">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-line pb-6">
          <div>
            <p className="text-[20px] font-semibold text-ink" style={{ color: marca?.color || undefined }}>{marca?.nombre}</p>
            {marca?.email && <p className="mt-1 text-[13px] text-ink-secondary">{marca.email}</p>}
            {marca?.telefono && <p className="text-[13px] text-ink-secondary">{marca.telefono}</p>}
          </div>
          <div className="text-right">
            <p className="text-[12px] uppercase tracking-[0.08em] text-ink-tertiary">Invoice</p>
            <p className="font-mono text-[22px] font-semibold text-ink" data-testid="public-invoice-number">{data.numero}</p>
            <p className={`mt-1 inline-block border px-2 py-0.5 text-[12px] font-semibold ${data.pagada ? "border-state-ok text-state-ok" : "border-line-strong text-ink"}`}
              data-testid="public-invoice-status">
              {data.pagada ? "Paid" : "Payment due"}
            </p>
          </div>
        </div>

        <div className="grid gap-6 border-b border-line py-6 sm:grid-cols-2">
          <div>
            <p className="text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">Bill to</p>
            {cliente?.compania && <p className="mt-1 text-[14px] font-semibold text-ink">{cliente.compania}</p>}
            <p className="text-[14px] text-ink">{cliente?.edificio}</p>
            {cliente?.direccion && <p className="text-[13px] text-ink-secondary">{formatAddress(cliente.direccion)}</p>}
            {cliente?.unidad && <p className="text-[13px] text-ink-secondary">Unit {cliente.unidad}</p>}
          </div>
          <div className="space-y-1 text-[13px] sm:text-right">
            <p><span className="text-ink-tertiary">Invoice date: </span><span className="text-ink">{fecha(data.fecha_emision)}</span></p>
            <p><span className="text-ink-tertiary">Due date: </span><span className="text-ink">{fecha(data.vence)}</span>
              {data.condiciones_dias ? <span className="text-ink-tertiary"> (Net {data.condiciones_dias})</span> : null}</p>
            {data.pagada && data.fecha_cobro && (
              <p><span className="text-ink-tertiary">Paid on: </span><span className="text-ink">{fecha(data.fecha_cobro)}</span></p>
            )}
            {orden?.codigo && <p><span className="text-ink-tertiary">Work order: </span><span className="font-mono text-ink">{orden.codigo}</span></p>}
          </div>
        </div>

        <table className="mt-6 w-full text-[13.5px]" data-testid="public-invoice-lines">
          <thead>
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data.lineas || []).map((l, i) => (
              <tr key={i} className="border-b border-line">
                <td className="py-2.5 pr-4 text-ink">{l.descripcion}</td>
                <td className="py-2.5 text-right tabular-nums text-ink">{formatMoney(l.monto_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <div className="w-full max-w-[260px] space-y-1.5 text-[14px]">
            <div className="flex justify-between"><span className="text-ink-secondary">Total</span>
              <span className="tabular-nums font-semibold text-ink" data-testid="public-invoice-total">{formatMoney(data.total_cents)}</span></div>
            <div className="flex justify-between border-t border-line pt-1.5"><span className="font-semibold text-ink">Balance due</span>
              <span className="tabular-nums font-semibold text-ink" data-testid="public-invoice-balance">{formatMoney(saldo)}</span></div>
          </div>
        </div>

        {data.nota && <p className="mt-6 text-[12.5px] text-ink-secondary">{data.nota}</p>}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 text-[12.5px] text-ink-tertiary">
          <p>Questions about this invoice? {marca?.email ? `Write to ${marca.email}` : "Reply to our email"}{marca?.telefono ? ` or call ${marca.telefono}` : ""}.</p>
          <button type="button" onClick={() => window.print()} data-testid="public-invoice-print"
            className="flex items-center gap-1.5 border border-line px-3 py-1.5 text-[12.5px] font-medium text-ink hover:bg-surface-sunken print:hidden">
            <Printer className="h-3.5 w-3.5" /> Print or save as PDF
          </button>
        </div>
      </div>
    </div>
  );
}
